import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Footer } from '../../Components/Footer';
import {
  useIsRoomExistQuery,
  useRoomAssignListingLazyQuery,
} from '../../generated/graphql';
import { buildPreCheckInUrl } from '../../Components/helper/preCheckInUrl';
import { pickFirstVcRoom } from '../../Components/helper/pickFirstVcRoom';
import Loading from '../../Components/Loading/Loading';
import { useSnackBar } from '../../Components/Hooks/useSnackBar';
import SnackBarMsg from '../../Components/SnackBar/SnackBarMsg';

const getErrorMessage = (err: any) =>
  err?.graphQLErrors?.[0]?.message ||
  err?.networkError?.result?.errors?.[0]?.message ||
  err?.clientErrors?.[0]?.message ||
  err?.message ||
  'Room assignment failed';

export const ChooseRoom = ({
  activeStep,
  setActiveStep,
  setAssignRoom,
  setRoomSelect,
  RegistrationDetails,
  isSelfChooseRoom,
  UpdateRoomAssign,
}) => {
  const [assigning, setAssigning] = useState(false);
  const { snackFunc, snackBarMessage, openSnackBar, close } = useSnackBar();

  const {
    loading: IsRoomExistLoadings,
    data: { IsRoomExist } = { IsRoomExist: null },
  } = useIsRoomExistQuery({
    variables: {
      HotelID: RegistrationDetails?.Booking?.HotelID,
      ArrivalDate: RegistrationDetails?.ArrivalDate,
      RoomTypeID: RegistrationDetails?.RoomTypeID,
    },
    fetchPolicy: 'no-cache',
  });

  const [
    RoomAssignListingQuery,
    { loading: RoomAssignListingLoadings, data: RoomAssignListingData },
  ] = useRoomAssignListingLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!RegistrationDetails?.Booking?.HotelID) return;

    RoomAssignListingQuery({
      variables: {
        HotelID: RegistrationDetails.Booking.HotelID,
        StartDate: RegistrationDetails.ArrivalDate,
        EndDate: RegistrationDetails.DepartureDate,
        RoomTypeID: RegistrationDetails.RoomTypeID,
      },
    });
  }, [
    RegistrationDetails?.Booking?.HotelID,
    RegistrationDetails?.ArrivalDate,
    RegistrationDetails?.DepartureDate,
    RegistrationDetails?.RoomTypeID,
    RoomAssignListingQuery,
  ]);

  const handleProceedWithoutChoosing = () => {
    if (assigning) return;

    // Prefer room already on the registration; otherwise pick first VC room
    const defaultRoom = RegistrationDetails?.RoomID
      ? {
          ...(RegistrationDetails?.Room || {}),
          ID: RegistrationDetails.RoomID,
          RoomTypeID:
            RegistrationDetails?.Room?.RoomTypeID || RegistrationDetails?.RoomTypeID,
          IsSelected: true,
        }
      : pickFirstVcRoom(RoomAssignListingData?.RoomAssignListing);

    if (!defaultRoom?.ID) {
      snackFunc('No rooms are available. Please contact the Front Desk for assistance.', false);
      return;
    }

    setAssigning(true);
    UpdateRoomAssign({
      variables: {
        BookingID: RegistrationDetails?.Booking?.ID,
        RegistrationID: RegistrationDetails?.ID,
        RoomID: defaultRoom.ID,
        HotelID: RegistrationDetails?.Booking?.HotelID,
        RoomTypeID: RegistrationDetails?.RoomTypeID,
      },
      onError: (err: any) => {
        setAssigning(false);
        snackFunc(getErrorMessage(err), false);
      },
      onCompleted: (data: any) => {
        setAssigning(false);
        if (data?.UpdateRoomAssignment === true) {
          setRoomSelect?.(defaultRoom);
          localStorage.setItem('SelectedRoom', JSON.stringify(defaultRoom));
          setActiveStep(activeStep + 1);
        } else {
          snackFunc('Room assignment failed', false);
        }
      },
    }).catch(() => {
      setAssigning(false);
    });
  };

  const showLoading = IsRoomExistLoadings || RoomAssignListingLoadings;

  return showLoading ? (
    <Loading />
  ) : (
    <>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          <Button
            variant="contained"
            style={{ minWidth: '220px' }}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#ff9800' },
              '&:active': { backgroundColor: '#ff9800' },
              color: '#ffffff',
              fontSize: '15px',
            }}
            onClick={() => setAssignRoom(true)}
            disabled={assigning || (isSelfChooseRoom === false || IsRoomExist === false)}
          >
            Choose Room
          </Button>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding="15px 0 0 0"
        >
          <Typography
            onClick={assigning ? undefined : handleProceedWithoutChoosing}
            fontSize="14px"
            fontWeight={300}
            color="#ff9800"
            sx={{ cursor: assigning ? 'default' : 'pointer', opacity: assigning ? 0.6 : 1 }}
          >
            {assigning ? 'Assigning room...' : 'Proceed without choosing Room'}
          </Typography>
          <KeyboardArrowRightIcon sx={{ fontSize: '16px', color: '#ff9800' }} />
        </Box>
      </Box>
      <Footer
        primary={true}
        options={[
          {
            name: 'Back',
            onClick: () => {
              if (activeStep !== 1) {
                setActiveStep(activeStep - 1);
              } else {
                window.location.href = buildPreCheckInUrl(
                  RegistrationDetails?.Booking?.HotelID,
                  RegistrationDetails?.Booking?.ID
                );
              }
            },
            disabled: assigning,
          },
        ]}
      />
      <SnackBarMsg open={openSnackBar} message={snackBarMessage} onClose={close} />
    </>
  );
};
